/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
/* Hooks de Redux */
import { useAppSelector, useAppDispatch } from "@redux/hooks";
import { RootState } from "@redux/store/store";
import { toggleMenu } from "@redux/slices/sideMenuSlice";
import { setSelectedProduct } from "@redux/slices/productSlice";

/* Iconos y componentes */
import { ReactComponent as HomeSvg } from "@assets/Icons/Icon.svg";
// import { ReactComponent as IconDocumentationSvg } from "@assets/book-open.svg";
import { ReactComponent as IconMenuSvg } from "@assets/Icons/IconMenu.svg";
import { ReactComponent as IconPlusSvg } from "@assets/Icons/IconPlusNV.svg";
// Iconos para add service en kontrol
import { ReactComponent as ServerSVG } from "@assets/Icons/IconServer.svg";
import { ReactComponent as DataInterconnectSVG } from "@assets/Icons/IconDataInterconnect.svg";
import { ReactComponent as InternetSVG } from "@assets/Icons/IconInternet.svg";

import { ReactComponent as IconLeft } from "@assets/Icons/DoubleLeft.svg";
import { ReactComponent as IconRight } from "@assets/Icons/Double-right.svg";

import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import MenuSection from "./components/MenuSection";

/* Data e utilidades del Menú */
import {
	mainItems,
	serviceItems,
	managementItems,
	settingsItems,
	MenuItem,
	reflectorV2MenuItems,
} from "./MenuData";

import {
	determineSelectedProduct,
	expandMatchingParents,
	getFilteredServiceItems,
	handleSelection,
} from "./MenuUtils";
import GenericLink from "@ui-components/Link/GenericLink";

/* Importamos helpers para la marca */
import { getBrand } from "@shared/components/utils/brandHelper";
import { getBrandLogosWithFallback } from "@shared/components/utils/brandConfig";

import AdminCenterMenu from './components/AdminCenterMenu';
import { adminSections } from "./components/AdminSections";
import { useTheme } from "theme/components/ThemeProvider";

/* === Mapeo ID => Nombre interno === */
function mapProductName(id: number): string {
	if (id === 1) return "CTX Reflector";
	if (id === 2) return "Kontrol Edge";
	if (id === 4) return "CTX Reflector V2";
	return "Unknown";
}

/* === Mapeo Nombre interno => Nombre de display === */
function mapDisplayName(internalName: string): string {
	switch (internalName) {
		case "CTX Reflector V2":
			return "Reflector";
		case "CTX Reflector":
			return "CTX Reflector";
		case "Kontrol Edge":
			return "Kontrol Edge";
		default:
			return internalName;
	}
}

/* === Opciones de servicios para "Add new service" en Kontrol Edge === */
type ServiceOption = {
	label: string;
	route: string;
	icon: React.ReactNode;
};

const serviceOptionsData: ServiceOption[] = [
	{
		label: "Add New Port",
		route: "/kontrol/add-port",
		icon: <ServerSVG className="h-5 w-5" />,
	},
	{
		label: "Add Connection",
		route: "/kontrol/add-ipx",
		icon: <InternetSVG className="h-5 w-5" />,
	},
	{
		label: "Add New Datacenter",
		route: "/kontrol/add-dc",
		icon: <DataInterconnectSVG className="h-5 w-5" />,
	},
];

const SideMenu: React.FC = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { pathname } = location;


	/* 1) Obtenemos 'userProfile' desde Redux */
	const userProfile = useAppSelector((state: RootState) => state.userProfile);
	const isExpanded = useAppSelector(
		(state: RootState) => state.sideMenu.isExpanded
	);
	const { TenantType: tenantType, canAccessAlerts } = userProfile;

	//Get current theme
	const { theme } = useTheme();                 
	const isDark = theme === 'theme-dark';

	/* 2) Obtenemos la marca y su logo */
	const brand = getBrand();
	
	// State for logos with fallback
	const [logos, setLogos] = useState({
		lightLogo: "",
		logo: "",
		nube: ""
	});
	const [logoSrc, setLogoSrc] = useState("");

	// Load logos with fallback validation
	useEffect(() => {
		const loadLogos = async () => {
			try {
				const brandLogos = await getBrandLogosWithFallback(brand);
				setLogos(brandLogos);
				setLogoSrc(isDark ? brandLogos.lightLogo : brandLogos.logo);
			} catch (error) {
				console.error("Error loading brand logos:", error);
			}
		};
		
		loadLogos();
	}, [brand]);

	useEffect(() => {
		if (logos.lightLogo && logos.logo) {
			setLogoSrc(isDark ? logos.lightLogo : logos.logo);
		}
	}, [isDark, logos.lightLogo, logos.logo]);

	// Use state to track the current image source and if fallback has been applied
	const [fallbackApplied, setFallbackApplied] = useState(false);
	
	//Function that is triggered if the image fails to load
	const handleImageError = () => {
		if (!fallbackApplied && logos.nube) {
			setFallbackApplied(true);
			setLogoSrc(logos.nube);
		}
	};
	
	/* 3) Derivamos las opciones de producto */
	const internalProductNames: string[] = userProfile.ProductList
		.map((p) => mapProductName(p.id))
		.filter((name) => name !== "Unknown");
	
	const productOptions: string[] = internalProductNames.map(mapDisplayName);
	
	// Create a mapping from display name to internal name for logic purposes
	const displayToInternalMap = useMemo(() => {
		const map: { [key: string]: string } = {};
		internalProductNames.forEach((internalName, index) => {
			map[productOptions[index]] = internalName;
		});
		return map;
	}, [internalProductNames, productOptions]);

	/* 4) Determinamos un valor por defecto para selectedProduct */
	const defaultProduct =
		productOptions.length > 0 ? productOptions[0] : "Select Product";

	/* 5) Estado local para el producto seleccionado */
	const [selectedProduct, setSelectedProductLocal] = useState(defaultProduct);

	/* Sincronizamos el producto seleccionado con la ruta actual */
	useEffect(() => {
		const routeProduct = determineSelectedProduct(location.pathname);
		// Convert route product to display name
		const routeDisplayName = mapDisplayName(routeProduct);
		if (productOptions.includes(routeDisplayName)) {
			setSelectedProductLocal(routeDisplayName);
			dispatch(setSelectedProduct(displayToInternalMap[routeDisplayName] || routeProduct));
		} else if (
			productOptions.length > 0 &&
			selectedProduct === "Select Product"
		) {
			// If we're not on a product route but have products, set to first product
			setSelectedProductLocal(productOptions[0]);
			dispatch(setSelectedProduct(displayToInternalMap[productOptions[0]]));
		}
	}, [location.pathname, productOptions]);
	/* Expand/collapse y tracking de item activo */
	const [expandedSections, setExpandedSections] = useState<{
		[key: string]: boolean;
	}>({});
	const [activeItem, setActiveItem] = useState<string>("");

	/* Filtrar items de servicios según el producto */
	const [filteredServiceItems, setFilteredServiceItems] = useState<MenuItem[]>(
		[]
	);
	useEffect(() => {
		const internalName = displayToInternalMap[selectedProduct] || selectedProduct;
		const filteredItems = getFilteredServiceItems(
			internalName,
			serviceItems
		);
		setFilteredServiceItems(filteredItems);
	}, [selectedProduct]);

	/* Botón para colapsar/expandir */
	const handleToggle = () => {
		dispatch(toggleMenu());
	};

	/* Expandimos los padres que hagan match con la ruta */
	useEffect(() => {
		const allItems = [
			...mainItems,
			...filteredServiceItems,
			...managementItems,
			...settingsItems,
		];
		expandMatchingParents(
			allItems,
			location.pathname,
			setExpandedSections,
			setActiveItem
		);
	}, [location.pathname, filteredServiceItems]);

	useEffect(() => {
		const internalName = displayToInternalMap[selectedProduct] || selectedProduct;
		if (internalName === "Kontrol Edge") {
			setActiveItem("Overview");
		}
	}, [selectedProduct]);

	/* onSelectProduct => si el usuario cambia en el dropdown de producto */
	const onSelectProduct = (selected: string) => {
		setSelectedProductLocal(selected);
		const internalName = displayToInternalMap[selected] || selected;
		dispatch(setSelectedProduct(internalName)); // update Redux global state with internal name
		handleSelection(internalName, navigate, setSelectedProductLocal); // use internal name for navigation
	};

	/* "Add new service" en Kontrol Edge */
	const serviceOptions = serviceOptionsData.map((option) => option.label);
	const [selectedOption, setSelectedOption] = useState<string>("");

	const onAddService = (selected: string) => {
		setSelectedOption(selected);
		const selectedOptionObj = serviceOptionsData.find(
			(option) => option.label === selected
		);
		if (selectedOptionObj) {
			navigate(selectedOptionObj.route);
		} else {
			console.warn(`No route defined for "${selected}"`);
		}
	};

	/* Determinamos la ruta para Admin Center según el producto */
	const internalName = useMemo(() => {
		return displayToInternalMap[selectedProduct] || selectedProduct;
	}, [displayToInternalMap, selectedProduct]);
	
	const adminCenterPath = useMemo(() => {
		return internalName === "CTX Reflector"
			? "/reflector/management/licensing"
			: internalName === "CTX Reflector V2"
				? "reflector-v2/admin-center/management/licensing"
				: "/kontrol/admin-center";
	}, [internalName]);

	/* Determinar si reemplazamos "Home" por "Overview" en caso de Kontrol Edge */
	const computedMainItems = useMemo(() => {
		return internalName === "Kontrol Edge"
			? [{ label: "Overview", path: "/kontrol", Icon: HomeSvg }]
			: mainItems;
	}, [internalName]);

	// Filter Settings → Account → Customers
	const filteredSettingsItems = useMemo(() => {
		return settingsItems.map((section) => {
		// only the "Account" section needs adjustment
		if (section.id === "account-management" && section.children) {
			return {
			...section,
			children: section.children.filter((child) => {
				// keep "Customers" only for GLOBAL_ADMIN
				if (child.id === "account-management-customers") {
				return tenantType === "GLOBAL_ADMIN";
				}
				if (child.id === "account-management-notifications") {
					return canAccessAlerts;
				}
				return true;
			}),
			};
		}
		return section;
		});
	}, [tenantType, canAccessAlerts]);
	
	//For the notifications module
	useEffect(() => {
		//Redirect if they're on the notifications URL
		if (
		pathname === "/kontrol/notifications-management" &&
		canAccessAlerts === false
	) {
		navigate("/", { replace: true });
	}
	}, [canAccessAlerts, navigate, pathname]);

	// State for showing Admin Center menu
	const [showAdminCenterMenu, setShowAdminCenterMenu] = useState(false);

	// Keep AdminCenterMenu open for any /kontrol/admin-center route
	useEffect(() => {
		if (location.pathname.startsWith('/kontrol/admin-center')) {
			setShowAdminCenterMenu(true);
		} else {
			setShowAdminCenterMenu(false);
		}
	}, [location.pathname]);


	return (
		<aside
			className={`
        relative
        ${isExpanded ? "w-[19rem]" : "w-[5rem]"}
        transition-all duration-300
        h-screen
        flex flex-col
        bg-semantic-bg-primary
        text-gray-700
        border border-semantic-border-primary
      `}
		>
			{showAdminCenterMenu ? (
				<AdminCenterMenu 
					onBack={() => setShowAdminCenterMenu(false)}
					onSectionClick={(route) => {
						if (route) {
							navigate(route);
						}
					}}
					activePath={location.pathname}
					sections={adminSections}
				/>
			) : (
				<>
				{/* Botón colapsar/expandir */}
				<button
					onClick={handleToggle}
					className={`
          absolute
          top-4
          right-[-12px]
          z-50
          w-8 h-8
          flex items-center justify-center
          rounded-full
          border border-semantic-border-secondary
          bg-semantic-bg-secondary
          text-semantic-text-secondary
          hover:opacity-80
        `}
				>
					{isExpanded ? <IconLeft /> : <IconRight />}
				</button>

				{/* Logo y dropdown de producto */}
				<div
					className={`flex flex-col items-center ${isExpanded ? "p-6" : "pt-16"}`}
				>
					{isExpanded && (
						<img
							src={logoSrc}
							alt="Brand Logo"
							className="w-auto text-semantic-text-primary mb-2 max-h-[80px]"
							onError={handleImageError}
						/>
					)}

					{isExpanded && productOptions.length > 0 && (
						<div className="mt-4">
							<GenericDropdown
								items={productOptions}
								defaultLabel="Select Product"
								value={selectedProduct}
								onSelect={onSelectProduct}
								minWidthClass="min-w-[240px]"
								maxWidthClass="100%"
							/>
						</div>
					)}

					{/* Botón "Add new service" si es Kontrol Edge */}
					{/* {selectedProduct === "Kontrol Edge" && (
						<GenericDropdown
							items={serviceOptions}
							value={selectedOption}
							disabled={true}
							onSelect={(selected) => onAddService(selected)}
							minWidthClass={isExpanded ? "min-w-[240px]" : "w-10"}
							className={`mt-5 ${isExpanded ? "w-full" : "w-10"}`}
							maxWidthClass={isExpanded ? "w-full" : "w-10"}
							buttonProps={{
								label: isExpanded ? "Add new service" : "",
								variant: "brand",
								icon: <IconPlusSvg className="h-6 w-6" />,
								iconPosition: "left",
							}}
							renderItem={(service) => {
								const option = serviceOptionsData.find(
									(o) => o.label === service
								);
								return (
									<button
										onClick={() => onAddService(service)}
										className={`
                    w-full text-left px-3 py-2 flex items-center gap-2 text-sm 
                    text-semantic-text-secondary hover:bg-semantic-state-active 
                    hover:text-semantic-text-brand transition-colors
                    ${
										service === "Add Connection"
											? "border-b border-semantic-border-secondary"
											: ""
									}
                  `}
									>
										{option && option.icon}
										{isExpanded && <span>{service}</span>}
									</button>
								);
							}}
						/>
					)} */}
				</div>

				{/* Secciones del menú */}
				<div className="flex-1 overflow-y-auto">
					<nav className="p-4 space-y-5">
						{internalName === "CTX Reflector V2" ? (
							// Render the new V2 menu structure
							<>
								{reflectorV2MenuItems.map((section: MenuItem) => (
									<MenuSection
										key={section.id || section.label}
										title={isExpanded ? section.label : ""}
										items={section.children ? section.children : [section]}
										expandedSections={expandedSections}
										setExpandedSections={setExpandedSections}
										activeItem={activeItem}
										setActiveItem={setActiveItem}
										isExpanded={isExpanded}
										onItemClick={(item) => item.path && navigate(item.path)}
									/>
								))}
							</>
						) : (
							<>
								<MenuSection
									title={isExpanded ? "Main" : ""}
									items={computedMainItems}
									expandedSections={expandedSections}
									setExpandedSections={setExpandedSections}
									activeItem={activeItem}
									setActiveItem={setActiveItem}
									onItemClick={(item) => item.path && navigate(item.path)}
									isExpanded={isExpanded}
								/>
								<MenuSection
									title={isExpanded ? "Services" : ""}
									items={filteredServiceItems}
									expandedSections={expandedSections}
									setExpandedSections={setExpandedSections}
									activeItem={activeItem}
									setActiveItem={setActiveItem}
									isExpanded={isExpanded}
									onItemClick={(item) => item.path && navigate(item.path)}
								/>
								{internalName === "CTX Reflector" && (
									<MenuSection
										title={isExpanded ? "Management" : ""}
										items={managementItems}
										expandedSections={expandedSections}
										setExpandedSections={setExpandedSections}
										activeItem={activeItem}
										setActiveItem={setActiveItem}
										isExpanded={isExpanded}
										onItemClick={(item) => item.path && navigate(item.path)}
									/>
								)}
								<MenuSection
									title={isExpanded ? "Settings" : ""}
									items={filteredSettingsItems}
									expandedSections={expandedSections}
									setExpandedSections={setExpandedSections}
									activeItem={activeItem}
									setActiveItem={setActiveItem}
									isExpanded={isExpanded}
									onItemClick={(item) => {
										if (item.label === "Customer Care") {
											window.Intercom?.("showNewMessage");
											return;
										}
										if (item.path) {
											navigate(item.path);
										}
									}}
								/>
							</>
						)}
					</nav>
				</div>

				{/* Bloque inferior: Admin Center + Documentation */}
				{[internalName, "CTX Reflector", "CTX Reflector V2", "Kontrol Edge"].includes(internalName) && (
				<div
					className="
          p-4
          flex
          flex-col
          items-center
          gap-4
          border-t
          border-solid
          border-semantic-border-secondary
        "
				>
					{tenantType === "GLOBAL_ADMIN" && (
						<GenericLink
							to={adminCenterPath}
							label={isExpanded ? "Admin Center" : ""}
							variant="brand"
							icon={<IconMenuSvg className="h-5 w-5 text-white" />}
							iconPosition={isExpanded ? "left" : undefined}
							className={`
								flex items-center
								${isExpanded ? "w-full justify-start px-3" : "w-[2.5rem] justify-center"}
								transition-all duration-300
							`}
							onClick={(e) => {
								if (internalName === 'Kontrol Edge') {
									e.preventDefault();
									setShowAdminCenterMenu(true);
									if (!location.pathname.startsWith('/kontrol/admin-center')) {
										navigate('/kontrol/admin-center/data-center');
									}
								}
							}}
						/>
					)}

					{/* <GenericLink
						to="/documentation"
						label={isExpanded ? "Documentation" : ""}
						shape="filled"
						variant="secondary"
						icon={<IconDocumentationSvg className="h-5 w-5" />}
						iconPosition={isExpanded ? "left" : undefined}
						className={`
							flex items-center text-black
							${isExpanded ? "w-full justify-start px-3" : "w-[2.5rem] justify-center"}
							transition-all duration-300
						`}
					/> */}
				</div>
				)}
				</>
			)}
		</aside>
	);
};

export default SideMenu;
